import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import DateRangePicker, {
  DateRangePickerDateSelectFunction,
  DateRangePickerProps,
  NullableDate,
} from '~components/Filters/DateRangePicker';
import { useStores } from '~store';
import { usePrevious } from '~utils/hooks/usePrevious';

interface SharedDatesFilterProps {
  dateRangePickerMode?: DateRangePickerProps['mode'];
  dateRangeUseTodayFormat?: DateRangePickerProps['useTodayFormat'];
}

const OrdersAssignedDatesFilter = observer(
  ({ dateRangePickerMode, dateRangeUseTodayFormat }: SharedDatesFilterProps) => {
    const { ordersAssignedStore } = useStores();

    const [initialStartDate, initialEndDate]: NullableDate[] = useMemo(() => {
      if (ordersAssignedStore.filters.startDate && ordersAssignedStore.filters.endDate) {
        const dateRange = [
          ordersAssignedStore.filters.startDate
            ? dayjs.tz(ordersAssignedStore.filters.startDate)
            : null,
          ordersAssignedStore.filters.endDate
            ? dayjs.tz(ordersAssignedStore.filters.endDate)
            : null,
        ];

        return dateRange;
      }

      const storedFilters = ordersAssignedStore.retrieveStoredFilters();
      if (storedFilters.startDate && storedFilters.endDate) {
        const dateRange = [
          dayjs.tz(storedFilters.startDate),
          dayjs.tz(storedFilters.endDate),
        ];

        return dateRange;
      }

      return [null, null];
    }, [ordersAssignedStore.filters.startDate, ordersAssignedStore.filters.endDate]);

    const [dateRange, setDateRange] = useState({
      start: initialStartDate,
      end: initialEndDate,
    });

    const handleDateFilterChange = useCallback<DateRangePickerDateSelectFunction>(
      (startDate, endDate) => {
        const start = startDate;
        const end = endDate ?? startDate;

        setDateRange({ start, end });
      },
      [],
    );

    const handleDateFilterClose = useCallback(() => {
      const startDate = dateRange.start?.format('YYYY-MM-DD');
      const endDate = dateRange.end?.format('YYYY-MM-DD');

      const startDateDidChange = ordersAssignedStore.filters.startDate !== startDate;
      const endDateDidChange = ordersAssignedStore.filters.endDate !== endDate;

      if (startDateDidChange || endDateDidChange) {
        ordersAssignedStore.setFilters({ startDate, endDate }, true);
      }
    }, [
      dateRange.start,
      dateRange.end,
      ordersAssignedStore.filters.startDate,
      ordersAssignedStore.filters.endDate,
    ]);

    const previousOrdersSearch = usePrevious(ordersAssignedStore.filters.search);
    const currentOrdersSearch = ordersAssignedStore.filters.search;

    useEffect(() => {
      const isSearchingOrders = Boolean(currentOrdersSearch?.length);
      const isClearingPreviousOrdersSearch =
        !isSearchingOrders && Boolean(previousOrdersSearch?.length);

      if (isSearchingOrders) {
        setDateRange({ start: null, end: null });
      } else if (isClearingPreviousOrdersSearch) {
        setDateRange({ start: initialStartDate, end: initialEndDate });
      }
    }, [previousOrdersSearch, currentOrdersSearch, initialStartDate, initialEndDate]);

    return (
      <DateRangePicker
        allowAllDates={true}
        endDate={dateRange.end}
        mode={dateRangePickerMode}
        onAnchorClose={handleDateFilterClose}
        onDateSelect={handleDateFilterChange}
        showShortcuts
        startDate={dateRange.start}
        useTodayFormat={dateRangeUseTodayFormat}
        variant="date_range"
      />
    );
  },
);

export default OrdersAssignedDatesFilter;
